
export type IssueCode =
   // Security
   'UNAUTHORIZED' |  // User must authenticate
   'FORBIDDEN' |     // User lacks access to perform the operation
   // Operation prevented because...
   'PARSE_INPUT' |   // Failed to parse the submitted payload
   'VALIDATION' |    // Submitted data, following static rules
   'BUSINESS_RULE' | // (e.g. no visits on discharged service)
   'NOT_FOUND' |     // Attempting an operation on an entity that does not exist
   'CONFLICT' |      // Submitted data is out of date
   'TOO_COSTLY' |    // Too costly to perform the operation (e.g. search with no filters)
   // Server / Network
   'SERVER_ERROR' |  // Uncaught exception thrown by the server
   'COMM_FAIL' |     // Failed to connect to the server
   // Catch-all
   'NOT_SPECIFIED';

function isIssueCode(code: string): code is IssueCode {
   switch (code) {
      case 'UNAUTHORIZED':
      case 'FORBIDDEN':
      case 'PARSE_INPUT':
      case 'VALIDATION':
      case 'BUSINESS_RULE':
      case 'NOT_FOUND':
      case 'CONFLICT':
      case 'TOO_COSTLY':
      case 'SERVER_ERROR':
      case 'COMM_FAIL':
      case 'NOT_SPECIFIED':
         return true;
   }
   return false;
}

export function issueTextOrDefault(issue: OperationIssue | null): string {
   if (issue?.details) {
      return issue.details;
   }
   switch (issue?.code) {
      case 'UNAUTHORIZED':
         return "Not authorized to access this resource.  Session may have expired.";
      case 'FORBIDDEN':
         return "Not authorized to access this resource.";
      case 'PARSE_INPUT':
         return "The server was unable to parse the submitted payload.";
      case 'VALIDATION':
         return "Submitted data failed at least one validation check.";
      case 'BUSINESS_RULE':
         break; // fall through to default shrug message. Server should have provided a message.
      case 'NOT_FOUND':
         return "Record not found.";
      case 'CONFLICT':
         return "Submitted data is out of date.  Try reloading the page.";
      case 'TOO_COSTLY':
         return "Attempted operation was too resource intensive.";
      case 'SERVER_ERROR':
         return "Error on server";
      case 'COMM_FAIL':
         return "Error communicating with the server";
      case 'NOT_SPECIFIED':
         break; // fall through to default shrug message.
   }
   return "An unexpected issue has occurred.";
}

export interface OperationIssue {
   readonly code: IssueCode;
   readonly details?: string;
   readonly path?: string;
}

function isOperationIssue(issue: any): issue is OperationIssue {
   return 'code' in issue && isIssueCode(issue.code);
}

export class OperationError extends Error {
   static from(payload: Payload): OperationError {
      if (payload.issues?.length) {
         return new OperationError().addIssues(payload.issues);
      }
      return new OperationError().addIssue({
         code: 'NOT_SPECIFIED'
      });
   }

   constructor() {
      super();
      Object.setPrototypeOf(this, new.target.prototype);
   }

   addIssue(newIssue: OperationIssue): OperationError {
      this.issues.push(newIssue);
      return this;
   }

   addIssues(newIssues: OperationIssue[]): OperationError {
      this.issues.push(...newIssues);
      return this;
   }

   readonly issues: OperationIssue[] = [];
}

// -----------------

export interface Payload {
   readonly success: boolean,
   readonly value?: any,
   readonly issues?: OperationIssue[]
}

export async function parsePayload(response: Response): Promise<Payload> {
   if (response.ok) {
      const json = await response.json().catch( () => {
         return {
            success: false,
            issues: [{
               code: 'PARSE_INPUT',
               details: "Failed to parse server response as JSON."
            }]
         };
      });
      if (!('success' in json)) {
         json.success = false;
      }
      // Ensure 'issues' is a non-empty array of valid OperationIssues, or remove it
      if ('issues' in json) {
         let issues: any[] = json.issues instanceof Array ? json.issues : [];
         issues = issues.filter(isOperationIssue);
         if (issues.length) {
            json.issues = issues; // Put the filtered, non-empty array back
         }
         else if (json.success) { // issues was a non-array or was empty and payload was success
            delete json.issues; // Just get rid of issues, remove both key and value
         }
      }
      // A non-success payload needs to have issues specified.
      if (!json.success && !json.issues?.length) {
         json.issues = [{
            code: 'NOT_SPECIFIED'
         }] as OperationIssue[]
      }
      return json as Payload;
   }
   const payloadFromResponse: Payload = {
      success: false,
      issues: [{
         code: response.status === 401 && 'UNAUTHORIZED' ||
            response.status === 404 && 'NOT_FOUND' ||
            response.status === 403 && 'FORBIDDEN' ||
            'SERVER_ERROR',
         details: response.statusText
      }]
   };
   return Promise.resolve(payloadFromResponse);
}

export function throwCommError(): Response {
   const commError: OperationError = new OperationError().addIssue({
      code: 'COMM_FAIL'
   });
   throw commError;
}
